import { useEffect, useState, useCallback, useMemo } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  HStack,
  Input,
  Select,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

export type IssueCategoryFormProps = {
  issueCategoryId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type ParentOption = { id: number; name: string }

const initialValues = {
  name: '',
  code: '',
  description: '',
  parent_id: '' as string | number,
  active: true,
}

/**
 * Issue Category form. Supports unlimited nesting (category > sub-category >
 * item, used by the ParentTicketCategoryCascade 3-level dropdown), but the
 * parent picker here is a flat select of all other categories rather than a
 * tree widget — simple and sufficient since most installs only use 2-3 levels.
 */
export function IssueCategoryForm({ issueCategoryId, onSuccess, onCancel }: IssueCategoryFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!issueCategoryId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)
  const [parentOptions, setParentOptions] = useState<ParentOption[]>([])
  const [parentsLoading, setParentsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setParentsLoading(true)
    apiClient
      .get('/service-catalog/issue-categories', { params: { per_page: 500 } })
      .then((res) => {
        if (cancelled) return
        const rows = Array.isArray(res.data?.data) ? res.data.data : []
        setParentOptions(rows.map((r: { id: number; name?: string }) => ({ id: Number(r.id), name: String(r.name ?? r.id) })))
      })
      .catch(() => {
        if (!cancelled) setParentOptions([])
      })
      .finally(() => {
        if (!cancelled) setParentsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Exclude self from the parent picker when editing (can't be your own parent)
  const selectableParents = useMemo(
    () => parentOptions.filter((p) => !issueCategoryId || p.id !== issueCategoryId),
    [parentOptions, issueCategoryId]
  )

  const loadOne = useCallback(async () => {
    if (!issueCategoryId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/service-catalog/issue-categories/${issueCategoryId}`)
      const r = data?.data ?? data
      setForm({
        name: r.name ?? '',
        code: r.code ?? '',
        description: r.description ?? '',
        parent_id: r.parent_id ?? '',
        active: r.active !== false,
      })
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [issueCategoryId, showError])

  useEffect(() => {
    if (issueCategoryId) {
      loadOne()
    } else {
      setForm(initialValues)
    }
  }, [issueCategoryId, loadOne])

  const updateField = (field: keyof typeof form, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim() || null,
        description: form.description.trim() || null,
        parent_id: form.parent_id === '' ? null : Number(form.parent_id),
        active: form.active,
      }
      if (issueCategoryId) {
        await apiClient.put(`/service-catalog/issue-categories/${issueCategoryId}`, payload)
        showSuccess('Success', 'Issue category updated successfully')
      } else {
        await apiClient.post('/service-catalog/issue-categories', payload)
        showSuccess('Success', 'Issue category created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') {
        setErrors(res.errors)
      } else {
        showError(err as Error)
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Box py={8}>Loading...</Box>

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.name?.length} isRequired>
            <Input
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="e.g. Hardware, Printer, Paper jam"
            />
            <FormErrorMessage>{errors.name?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Code</FormLabel>
          <FormControl isInvalid={!!errors.code?.length}>
            <Input
              value={form.code}
              onChange={(e) => updateField('code', e.target.value)}
              placeholder="Optional short code"
            />
            <FormErrorMessage>{errors.code?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Parent category</FormLabel>
          <FormControl isInvalid={!!errors.parent_id?.length}>
            <Select
              value={form.parent_id === null || form.parent_id === '' ? '' : String(form.parent_id)}
              onChange={(e) => updateField('parent_id', e.target.value === '' ? '' : e.target.value)}
              placeholder={parentsLoading ? 'Loading...' : 'None (top-level category)'}
              isDisabled={parentsLoading}
            >
              {selectableParents.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
            <FormHelperText>
              Leave as None for a top-level category (1st level of the Issue category / Sub-category / Item cascade).
            </FormHelperText>
            <FormErrorMessage>{errors.parent_id?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Description</FormLabel>
          <FormControl isInvalid={!!errors.description?.length}>
            <Textarea
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Optional description"
              rows={3}
            />
            <FormErrorMessage>{errors.description?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>Active</FormLabel>
          <FormControl>
            <Checkbox
              isChecked={form.active}
              onChange={(e) => updateField('active', e.target.checked)}
              colorScheme="blue"
            >
              <Box as="span">Available in the issue classification cascade on tickets.</Box>
            </Checkbox>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={saving}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={saving} loadingText={issueCategoryId ? 'Saving...' : 'Creating...'}>
            {issueCategoryId ? 'Save' : 'Create Issue Category'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
